import {Enum, EnumValue} from "../../utils/enum";
import {TFunction} from "i18next";
import {mdiFacebook} from "@mdi/js";
import {services} from "../service/services";
import {Colors, Icon} from "@blueprintjs/core";
import {TextHighlight} from "@eptica/vecko-js-commons";
import React, {CSSProperties} from "react";
import {feedbackHighlight} from "../../feedback/utils/feedbackHighlight";
import {FeedbackDTO, Offset} from "./index";
import {keepHighlightedSentences} from "../../feedback/utils/sentenceUtils";
import _ from 'lodash';
import {FeedbackDisplay} from "../../feedback/InnerFeedback";
import {Rating} from "@smastrom/react-rating";
import '@smastrom/react-rating/style.css'


const join = (array: Array<JSX.Element>, separator: JSX.Element, addTrailingSeparator: boolean = false): Array<JSX.Element> => {
    const result = [];
    const len = array.length;

    const item = (it, i) => {
        return React.cloneElement(it, {key: `item_${i}`});
    };

    const sep = (i) => {
        return React.cloneElement(separator, {key: `sep_${i}`});
    };

    if (len > 1 && addTrailingSeparator) {
        result.push(sep(0));
    }

    for (let i = 0; i < len; i++) {
        result.push(item(array[i], i));
        if (i < len - 1) {
            result.push(sep(i + 1));
        }
    }
    return result;
};

export class ChannelKind extends EnumValue {
    readonly icon: string;
    readonly conversational: boolean;
    readonly renderer: (display: FeedbackDisplay, feedback: FeedbackDTO, verbatimsRef: any[], t: TFunction, centerOnOffset: boolean, shouldHighlight?: (offset: Offset, selection?) => boolean) => Array<JSX.Element>;

    constructor(icon: string, conversational: boolean, renderer: (display: FeedbackDisplay, feedback: FeedbackDTO, verbatimsRef: any[], t: TFunction, centerOnOffset: false, shouldHighlight?: (offset: Offset, selection?) => boolean) => Array<JSX.Element>) {
        super();
        this.icon = icon;
        this.conversational = conversational;
        this.renderer = renderer;
    }

    getLabel(t: TFunction): string {
        return t(`channelKind.${this.name}`);
    }
}

class ChannelKindEnumType extends Enum<ChannelKind> {
    EMAIL = new ChannelKind('envelope', false, ChannelKindEnumType.renderVerbatims);
    SURVEY = new ChannelKind('form', false, ChannelKindEnumType.renderVerbatimsForSurvey);
    CHAT = new ChannelKind('chat', true, ChannelKindEnumType.renderConversational);
    FACEBOOK = new ChannelKind(mdiFacebook, false, ChannelKindEnumType.renderVerbatims);
    PHONE = new ChannelKind('headset', true, ChannelKindEnumType.renderConversational);
    WEBMAIL = new ChannelKind('form', false, ChannelKindEnumType.renderVerbatims);

    constructor() {
        super();
        this.initEnum('ChannelKind');
    }


    static renderConversational(display: FeedbackDisplay | FeedbackDisplay.SMALL, feedback: FeedbackDTO, verbatimsRef: any[], t: TFunction, centerOnOffset: boolean, shouldHighlight?: (offset: Offset, selection?) => boolean) {
        if (display === FeedbackDisplay.SMALL || !display) return ChannelKindEnumType.renderVerbatims(display, feedback, verbatimsRef, t, centerOnOffset, shouldHighlight);
        return feedback.verbatims.map((verbatim, i) => {

            const offsets = (verbatim.offsets || []).filter(offset => shouldHighlight(offset));

            const textStyle: CSSProperties = {
                display: 'inline-block',
                marginLeft: 10,
                marginRight: 10,
                marginBottom: 10,
                marginTop: 4,
                padding: 5,
                border: `1px solid ${Colors.GRAY5}`,
                borderRadius: '0 4px 4px 4px',
                backgroundColor: Colors.LIGHT_GRAY5,
                fontSize: 16,
                lineHeight: 1.5,
                maxWidth: '80%',
                textAlign: "left"
            };

            if (!verbatim.customer) {
                textStyle.color = Colors.GRAY3;
            }

            const wrapperStyle = {
                textAlign: verbatim.customer ? 'left' as const : 'right' as const
            };

            var verbatimRef: any = React.createRef();
            verbatimsRef.push({verbatim: verbatim, ref: verbatimRef});
            return <div key={i} style={wrapperStyle}>
                <div>{verbatim.sender}</div>
                <div style={textStyle} ref={verbatimRef}>
                    <TextHighlight text={verbatim.content}
                                   offsets={offsets}
                                   highlighter={(offset, text) => feedbackHighlight(offset, text, t)}/>
                </div>
            </div>;
        });
    }

    static renderVerbatims(display: FeedbackDisplay | FeedbackDisplay.SMALL, feedback: FeedbackDTO, verbatimsRef: any[], t: TFunction, centerOnOffset: boolean, shouldHighlight?: (offset: Offset, selection?) => boolean) {
        const channelKind = services.getFeedbackService().getChannelKind(feedback);
        const separator = channelKind?.conversational ?
            <Icon icon="caret-right" style={{verticalAlign: 'middle'}}/> : <br/>;
        const addTrailingSeparator = channelKind ? channelKind.conversational : false;

        return join(services.getFeedbackService().getRelevantVerbatims(feedback)
                // we do not display verbatims that have no offsets
                .filter(verbatim => !centerOnOffset || !_.isEmpty(verbatim.offsets))
                // keep only sentences that start on the first offset
                .map(verbatim => {
                    if (centerOnOffset && services.getFeedbackService().hasOffset(feedback)) {
                        return keepHighlightedSentences(verbatim.content, verbatim.offsets, verbatim.sentences);
                    }
                    return verbatim
                })
                .map(verbatim => {
                    const offsets = (verbatim.offsets || []).filter(offset => shouldHighlight(offset));
                    return <>
                        {verbatim['truncatedLeft'] ? '[...] ' : null}
                        <TextHighlight text={verbatim.content}
                                       offsets={offsets}
                                       highlighter={(offset, text) => feedbackHighlight(offset, text, t)}/>
                    </>
                }),
            separator, addTrailingSeparator);
    }

    static renderVerbatimsForSurvey(display: FeedbackDisplay, feedback: FeedbackDTO, verbatimsRef: any[], t: TFunction, centerOnOffset: boolean, shouldHighlight?: (offset: Offset, selection?) => boolean) {
        if (display === FeedbackDisplay.SMALL || !display) return ChannelKindEnumType.renderVerbatims(display, feedback, verbatimsRef, t, centerOnOffset, shouldHighlight);
        return feedback.verbatims.map((verbatim, i) => {

            const offsets = (verbatim.offsets || []).filter(offset => shouldHighlight(offset));

            const textStyle: CSSProperties = {
                display: 'inline-block',
                marginLeft: 10,
                marginRight: 10,
                marginBottom: 10,
                marginTop: 4,
                padding: 5,
                border: `1px solid ${Colors.GRAY5}`,
                borderRadius: '0 4px 4px 4px',
                backgroundColor: Colors.LIGHT_GRAY5,
                fontSize: 16,
                lineHeight: 1.5,
                width: '100%',
                textAlign: "left"
            };


            var verbatimRef: any = React.createRef();
            verbatimsRef.push({verbatim: verbatim, ref: verbatimRef});
            return <div key={i} style={{textAlign: "left"}}>
                <div style={{display: 'inline-flex', width:'100%'}}>{verbatim.metadata?.custom?.question}
                    {verbatim.metadata?.custom?.score ?
                        <Rating style={{maxWidth: 90, marginLeft: '15px'}} readOnly
                                value={verbatim.metadata.custom.score as any}/> : null}
                </div>
                <div style={textStyle} ref={verbatimRef}>
                    <TextHighlight text={verbatim.content}
                                   offsets={offsets}
                                   highlighter={(offset, text) => feedbackHighlight(offset, text, t)}/>
                </div>
            </div>;
        });
    }
}

export const ChannelKindEnum = new ChannelKindEnumType();